import { useState } from 'react'
import { supabase } from '../lib/supabase'
import type { CreateUserRequest, CreateUserResponse } from '../types/database'

interface CreateUserModalProps {
  onClose: () => void
  onSuccess: () => void
}

export function CreateUserModal({ onClose, onSuccess }: CreateUserModalProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [createdPassword, setCreatedPassword] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  const getErrorMessage = (error: string): string => {
    if (error.includes('already registered')) {
      return '이메일이 이미 존재합니다'
    } else if (error.includes('Invalid email')) {
      return '이메일 형식이 올바르지 않습니다'
    } else if (error.includes('Password too weak')) {
      return '비밀번호는 최소 6자 이상이어야 합니다'
    } else if (error.includes('Missing required fields')) {
      return '모든 필드를 입력해주세요'
    } else if (error.includes('Forbidden')) {
      return '사용자 생성 권한이 없습니다'
    } else if (error.includes('Unauthorized')) {
      return '로그인이 필요합니다'
    } else {
      return '사용자 생성에 실패했습니다'
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccessMessage('')
    setCreatedPassword('')
    setLoading(true)

    try {
      // Validate all fields are filled
      if (!email || !firstName || !lastName) {
        throw new Error('Missing required fields')
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        throw new Error('Invalid email')
      }

      // Validate password length if provided
      if (password && password.length < 6) {
        throw new Error('Password too weak')
      }

      // Call the Edge Function to create the user
      const requestBody: CreateUserRequest = {
        email: email.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      }

      if (password) {
        requestBody.password = password
      }

      const { data, error: functionError } = await supabase.functions.invoke<CreateUserResponse>(
        'create-user',
        {
          body: requestBody,
        }
      )

      if (functionError) {
        throw functionError
      }

      if (data?.error) {
        throw new Error(data.error)
      }

      // Success
      setSuccessMessage('사용자가 성공적으로 생성되었습니다')
      if (data?.password) {
        setCreatedPassword(data.password)
      } else {
        // If no password returned (should not happen with our backend logic but safe fallback)
        onSuccess()
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '네트워크 오류가 발생했습니다'
      setError(getErrorMessage(errorMessage))
    } finally {
      setLoading(false)
    }
  }

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(createdPassword)
    alert('비밀번호가 복사되었습니다')
  }

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4 font-display">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-light">
              <span className="material-symbols-outlined text-primary">person_add</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900">사용자 생성</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-base">error</span>
              <span className="text-sm">{error}</span>
            </div>
          )}

          {successMessage && !createdPassword && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-base">check_circle</span>
              <span className="text-sm">{successMessage}</span>
            </div>
          )}

          {createdPassword ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-base">check_circle</span>
                  <span className="font-medium">사용자 생성 완료</span>
                </div>
                <p className="text-sm">아래 비밀번호를 복사하여 사용자에게 전달해주세요. 이 창을 닫으면 다시 확인할 수 없습니다.</p>
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">생성된 비밀번호</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={createdPassword}
                    className="form-input block w-full px-4 py-2.5 border-gray-200 rounded-lg text-gray-900 bg-gray-50 font-mono"
                  />
                  <button
                    type="button"
                    onClick={handleCopyPassword}
                    className="flex items-center justify-center px-4 py-2.5 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    title="복사"
                  >
                    <span className="material-symbols-outlined">content_copy</span>
                  </button>
                </div>
              </div>

              <div className="flex justify-end mt-6 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onSuccess}
                  className="flex items-center gap-2 px-6 py-2.5 border border-transparent rounded-lg text-sm font-bold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                >
                  <span>확인</span>
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-5">
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    성
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="material-symbols-outlined text-gray-400 text-sm">person</span>
                    </div>
                    <input
                      id="lastName"
                      type="text"
                      required
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      disabled={loading}
                      className="form-input block w-full pl-10 px-4 py-2.5 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary disabled:bg-gray-50 disabled:cursor-not-allowed"
                      placeholder="성을 입력하세요"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    이름
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="material-symbols-outlined text-gray-400 text-sm">person</span>
                    </div>
                    <input
                      id="firstName"
                      type="text"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      disabled={loading}
                      className="form-input block w-full pl-10 px-4 py-2.5 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary disabled:bg-gray-50 disabled:cursor-not-allowed"
                      placeholder="이름을 입력하세요"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    이메일
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="material-symbols-outlined text-gray-400 text-sm">mail</span>
                    </div>
                    <input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      className="form-input block w-full pl-10 px-4 py-2.5 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary disabled:bg-gray-50 disabled:cursor-not-allowed"
                      placeholder="user@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    비밀번호 <span className="text-gray-400 font-normal">(선택사항)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="material-symbols-outlined text-gray-400 text-sm">key</span>
                    </div>
                    <input
                      id="password"
                      type="password"
                      minLength={6}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      className="form-input block w-full pl-10 px-4 py-2.5 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary disabled:bg-gray-50 disabled:cursor-not-allowed"
                      placeholder="입력하지 않으면 자동 생성됩니다"
                    />
                  </div>
                  {password && password.length > 0 && (
                    <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                      <span className="material-symbols-outlined text-sm">info</span>
                      <span>최소 6자 이상</span>
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="material-symbols-outlined text-base">close</span>
                  <span>취소</span>
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2.5 border border-transparent rounded-lg text-sm font-bold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>
                      <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
                      <span>생성 중...</span>
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined text-base">add</span>
                      <span>생성</span>
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  )
}
